import { UseQueryResult } from "@tanstack/react-query";

import { persistentQueryClient } from "utils";

import { GET_CARBON_PROGRAM_OUTCOMES, GET_FIELDS, GET_FIELD_PRACTICES } from "consts";

import { FieldListField } from "models";

export const queryIsSuccess = (query: UseQueryResult): boolean => query.isSuccess;

export const queryIsPending = (query: UseQueryResult): boolean => query.isPending;

export const queryIsFetching = (query: UseQueryResult): boolean => query.isFetching;

export const queryIsLoading = (query: UseQueryResult): boolean => query.isLoading;

export const invalidateCarbonProgramQueriesByChunk = (fieldId: string, simulationId?: string) => {
  // get the metadata for the GET_CARBON_PROGRAM_OUTCOMES query
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const queryKeys = queryCache
    .getAll()
    .map((query) => query.queryKey)
    .filter(([firstKey]) => firstKey === GET_CARBON_PROGRAM_OUTCOMES);

  // find the query keys (format [GET_CARBON_PROGRAM_OUTCOMES, fieldId, simulationId]) which relate to the chunk the field and the simulation are in
  const queryKeyToInvalidate = queryKeys.filter(([_key, fieldIdsChunk, simulationIdsChunk]) => {
    if (simulationId) {
      return (fieldIdsChunk as string[]).includes(fieldId) && simulationIdsChunk && (simulationIdsChunk as string[]).includes(simulationId);
    }
    return (fieldIdsChunk as string[]).includes(fieldId) && !simulationIdsChunk;
  });

  queryKeyToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

export const invalidateQueriesByChunk = (queryKey: string, dependencyId: string) => {
  // get the metadata for the provided query key
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const queryKeys = queryCache
    .getAll()
    .map((query) => query.queryKey)
    .filter(([firstKey]) => firstKey === queryKey);

  // find the query keys (format [queryKey, dependencyId]) which relate to the chunk the dependency is in
  const queryKeyToInvalidate = queryKeys.filter(([_key, fieldIdsChunk]) => (fieldIdsChunk as string[]).includes(dependencyId));

  queryKeyToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};

export const invalidateFieldPracticesByFarmId = (farmId: string) => {
  const queryClient = persistentQueryClient;
  const queryCache = queryClient.getQueryCache();
  const allQueries = queryCache.getAll();

  const farmFieldsList = allQueries.filter((query) => query.queryKey[0] === GET_FIELDS && query.queryKey[1] === farmId)?.[0]?.state.data as
    | FieldListField[]
    | undefined;
  if (!farmFieldsList) return;

  const fieldPracticesQueryKeysToInvalidate = allQueries
    .filter(
      (query) => query.queryKey[0] === GET_FIELD_PRACTICES && !!query.queryKey[1] && (typeof query.queryKey[1] === "string" || Array.isArray(query.queryKey[1]))
    )
    .filter((filteredQuery) => {
      if (typeof filteredQuery.queryKey[1] === "string") {
        return farmFieldsList.some((field) => field.id === filteredQuery.queryKey[1]);
      }
      return (filteredQuery.queryKey[1] as unknown as string[]).some((queryFieldId) => farmFieldsList.some((field) => field.id === queryFieldId));
    })
    .map((query) => query.queryKey);

  fieldPracticesQueryKeysToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: key }));
};
